import { selectionEquals } from "./selection/equals";
import { resolveSelection } from "./selection/resolve";
import type { EmailDocument, Selection } from "./types";

export const HISTORY_LIMIT = 60;

export type HistorySnapshot = {
  document: EmailDocument;
  selection: Selection;
};

export function cloneDocument(doc: EmailDocument): EmailDocument {
  return structuredClone(doc);
}

export function cloneSelection(selection: Selection): Selection {
  return structuredClone(selection);
}

export function cloneSnapshot(snapshot: HistorySnapshot): HistorySnapshot {
  return {
    document: cloneDocument(snapshot.document),
    selection: cloneSelection(snapshot.selection),
  };
}

type HistoryState = {
  past: HistorySnapshot[];
  future: HistorySnapshot[];
  document: EmailDocument;
  selection: Selection;
};

export function resolveHistoryNavigationSelection(
  document: EmailDocument,
  snapshotSelection: Selection,
  currentSelection: Selection,
): Selection {
  const resolvedSnapshot = resolveSelection(document, snapshotSelection);
  const resolvedCurrent = resolveSelection(document, currentSelection);

  if (selectionEquals(resolvedSnapshot, resolvedCurrent)) {
    return resolvedCurrent;
  }

  return resolvedSnapshot;
}

export function undoHistory(state: HistoryState): boolean {
  const previous = state.past.pop();
  if (!previous) {
    return false;
  }
  const currentSelection = state.selection;
  state.future.push(
    cloneSnapshot({ document: state.document, selection: state.selection }),
  );
  state.document = previous.document;
  state.selection = resolveHistoryNavigationSelection(
    previous.document,
    previous.selection,
    currentSelection,
  );
  return true;
}

export function redoHistory(state: HistoryState): boolean {
  const next = state.future.pop();
  if (!next) {
    return false;
  }
  const currentSelection = state.selection;
  state.past.push(
    cloneSnapshot({ document: state.document, selection: state.selection }),
  );
  state.document = next.document;
  state.selection = resolveHistoryNavigationSelection(
    next.document,
    next.selection,
    currentSelection,
  );
  return true;
}
